import {
	CustomEmojiData,
	RawEventRSVPOption,
	GuildData,
	OptionPreset,
} from "./sesh_types";
import { Constants } from "./constants";
import { cloneDeep } from "lodash";

export namespace common_format_utils {
	export function get_stringified_emoji_for_embed(
		emoji: Pick<CustomEmojiData, "id" | "name" | "animated">
	) {
		const a = emoji.animated ? "a" : "";
		return `<${a}:${emoji.name}:${emoji.id}>`;
	}

	export function get_stringified_emoji_for_reaction(emoji: CustomEmojiData) {
		const a = emoji.animated ? "a:" : "";
		return `${a}${emoji.name}:${emoji.id}`;
	}

	export function get_unused_not_found_emoji(options: RawEventRSVPOption[]) {
		const potential_not_found_emojis: CustomEmojiData[] = cloneDeep(
			Constants.SESH_CUSTOM_EMOJIS.NOT_FOUND
		);
		for (const option of options) {
			let i = 0;
			for (const not_found_emoji of potential_not_found_emojis) {
				if (option.emoji === not_found_emoji.id) {
					potential_not_found_emojis.splice(i, 1);
					break;
				}
				i++;
			}
		}

		if (potential_not_found_emojis.length > 0) {
			return potential_not_found_emojis[0];
		}
	}

	export function get_guild_icon_url(guild_data: GuildData) {
		if (!guild_data.icon) {
			return;
		}
		return `https://cdn.discordapp.com/icons/${guild_data.id}/${guild_data.icon}.png`;
	}

	export function get_duplicate_emojis_in_preset(preset: OptionPreset) {
		for (const option of preset.custom_options) {
			const filter_result = preset.custom_options.filter(
				(value) => value.emoji === option.emoji
			);

			if (filter_result.length > 1) {
				return option.emoji;
			}
		}
		return null;
	}

	// Place location utilities
	export interface PlaceLocationData {
		type: "place";
		placeId: string;
		name: string;
		address: string;
	}

	export function parsePlaceLocation(location: string): PlaceLocationData | null {
		try {
			const parsed = JSON.parse(location);
			if (parsed && parsed.type === "place" && parsed.placeId) {
				return parsed as PlaceLocationData;
			}
		} catch {
			// Not JSON or not a place
		}
		return null;
	}

	/**
	 * Returns the display text for a location.
	 * For places, returns "Name, Address" format.
	 * For other locations, returns the raw value.
	 */
	export function getLocationDisplayText(location: string): string {
		const placeData = parsePlaceLocation(location);
		if (placeData) {
			if (placeData.address) {
				return `${placeData.name}, ${placeData.address}`;
			}
			return placeData.name;
		}
		return location;
	}

	/**
	 * Returns the Google Maps URL for a place location, or null for non-place locations.
	 */
	export function getLocationMapsUrl(location: string): string | null {
		const placeData = parsePlaceLocation(location);
		if (placeData) {
			// Use Google's officially supported, cross-platform Maps URL format
			// (api=1). The legacy `/maps/place/?q=place_id:` format only resolves
			// on the Maps website and breaks in the mobile apps (the place ID gets
			// treated as a literal search string). `query` is required and acts as
			// the human-readable fallback; `query_place_id` pins the exact place.
			// https://developers.google.com/maps/documentation/urls/get-started
			const query = placeData.address
				? `${placeData.name}, ${placeData.address}`
				: placeData.name;
			return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
				query
			)}&query_place_id=${encodeURIComponent(placeData.placeId)}`;
		}
		return null;
	}
}
